/**
 * PatientListPage Component
 *
 * Modern list view for patients with search, pagination, and payer filtering.
 * Desktop: two-column layout with payer stats sidebar on the left.
 * Mobile: single column (sidebar hidden).
 */

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetPatientVisibilityQuery } from '@features/organizations';
import { useSubjectLanguage, useUserRoles } from '@features/user';
import {
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Clear as ClearIcon,
  FileUpload as FileUploadIcon,
  MergeType as MergeIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useFeatureGate } from '@services/statsig';
import { hasAdminPrivileges } from '@utils/roleUtils';

import NewPatientModal from '../../../components/Patient/NewPatientModal';
import { capitalize } from '../../../utils/utils';
import { usePatientList } from '../hooks';

import { PatientEmptyState } from './PatientEmptyState';
import { PatientListRow } from './PatientListRow';
import { PayerStatsSidebar } from './PayerStatsSidebar';

export const PatientListPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const subjectLanguage = useSubjectLanguage();
  const roles = useUserRoles();
  const isAdmin = hasAdminPrivileges(roles);
  const showMergeButton = useFeatureGate('feature_patient_deduplication');
  const showImportButton = useFeatureGate('feature_patient_import');
  const { data: visibilityData } = useGetPatientVisibilityQuery();
  const isOrgWide = visibilityData?.mode === 'organization_wide';
  const [newPatientModalOpen, setNewPatientModalOpen] = useState(false);

  const {
    patients,
    isLoading,
    error,
    searchTerm,
    page,
    totalRows,
    hasNextPage,
    hasPreviousPage,
    isEmpty,
    isSearchActive,
    activeFilter,
    setSearchTerm,
    setPage,
    clearSearch,
    setPayerFilter,
    setCoverageBucketFilter,
    clearFilter,
    navigateToPatient,
    handlePatientCreated,
  } = usePatientList({ initialPageSize: 20 });

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    [setSearchTerm],
  );

  const handleImportClick = useCallback(() => {
    navigate('/patients/import');
  }, [navigate]);

  const handleNewPatientOpen = useCallback(() => {
    setNewPatientModalOpen(true);
  }, []);

  const handleNewPatientClose = useCallback(() => {
    setNewPatientModalOpen(false);
  }, []);

  const handlePatientSuccess = useCallback(
    (patientId: string) => {
      handlePatientCreated(patientId);
      setNewPatientModalOpen(false);
    },
    [handlePatientCreated],
  );

  const handlePreviousPage = useCallback(() => {
    setPage(page - 1);
  }, [setPage, page]);

  const handleNextPage = useCallback(() => {
    setPage(page + 1);
  }, [setPage, page]);

  // Build the active filter label for the chip
  const filterLabel = activeFilter
    ? activeFilter.type === 'payer'
      ? activeFilter.payer_name
      : activeFilter.bucket === 'needs_review'
        ? 'Needs Review'
        : 'No Coverage'
    : null;

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Error loading {subjectLanguage}s</AlertTitle>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 1280,
        mx: 'auto',
        p: { xs: 2, sm: 3 },
        display: 'flex',
        gap: 3,
        alignItems: 'flex-start',
      }}
    >
      {/* Payer Stats Sidebar — desktop only */}
      <PayerStatsSidebar
        activeFilter={activeFilter}
        onPayerSelect={setPayerFilter}
        onBucketSelect={setCoverageBucketFilter}
        onClearFilter={clearFilter}
        disabled={isSearchActive}
      />

      {/* Main content column */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: theme.palette.text.primary }}
            >
              {capitalize(subjectLanguage)}s
            </Typography>
            {!isLoading && (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.25 }}>
                {totalRows.toLocaleString()} {totalRows === 1 ? subjectLanguage : `${subjectLanguage}s`}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {showMergeButton && isAdmin && (
              <Tooltip
                title={!isOrgWide ? 'Enable organization-wide patient sharing in Practice Settings to use this feature' : ''}
                arrow
              >
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<MergeIcon />}
                    onClick={() => navigate('/patients/merge')}
                    disabled={!isOrgWide}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2.5,
                      borderRadius: 2,
                    }}
                  >
                    Merge Patients
                  </Button>
                </span>
              </Tooltip>
            )}
            {showImportButton && isAdmin && (
              <Tooltip
                title={!isOrgWide ? 'Enable organization-wide patient sharing in Practice Settings to use this feature' : ''}
                arrow
              >
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<FileUploadIcon />}
                    onClick={handleImportClick}
                    disabled={!isOrgWide}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2.5,
                      borderRadius: 2,
                    }}
                  >
                    Import Patients
                  </Button>
                </span>
              </Tooltip>
            )}

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNewPatientOpen}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                borderRadius: 2,
              }}
            >
              New {capitalize(subjectLanguage)}
            </Button>
          </Box>
        </Box>

        {/* Search */}
        <TextField
          fullWidth
          placeholder={`Search ${subjectLanguage}s by name...`}
          value={searchTerm}
          onChange={handleSearchChange}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.grey[400] }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch} aria-label="Clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'background.paper',
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.grey[400],
                },
              },
            },
          }}
        />

        {/* Active filter chip */}
        {filterLabel && (
          <Box sx={{ mb: 1.5 }}>
            <Chip
              label={`Filtered: ${filterLabel}`}
              onDelete={clearFilter}
              color={
                activeFilter?.type === 'coverage_bucket' && activeFilter.bucket === 'no_coverage'
                  ? 'error'
                  : activeFilter?.type === 'coverage_bucket'
                    ? 'warning'
                    : 'primary'
              }
              variant="outlined"
              size="small"
              sx={{ fontWeight: 500 }}
            />
          </Box>
        )}

        {/* List Container */}
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* Loading state */}
          {isLoading && patients.length === 0 ? (
            <Box>
              {Array.from({ length: 8 }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 3,
                    py: 2,
                    borderBottom: `1px solid ${theme.palette.grey[100]}`,
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Skeleton variant="circular" width={44} height={44} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="60%" height={20} sx={{ mb: 0.5 }} />
                    <Skeleton width="30%" height={16} />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : isEmpty ? (
            <PatientEmptyState
              isSearchActive={isSearchActive}
              searchTerm={searchTerm}
              onNewPatient={handleNewPatientOpen}
              onClearSearch={clearSearch}
            />
          ) : (
            <>
              {/* List */}
              <Box sx={{ position: 'relative' }}>
                {isLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: alpha(theme.palette.background.paper, 0.7),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}

                {patients.map((patient) => (
                  <PatientListRow
                    key={patient.patient_id}
                    patient={patient}
                    onSelect={navigateToPatient}
                  />
                ))}
              </Box>

              {/* Pagination */}
              {totalRows > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 1.5,
                    borderTop: `1px solid ${theme.palette.grey[100]}`,
                    bgcolor: alpha(theme.palette.grey[50], 0.5),
                  }}
                >
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    Page {page + 1} of {Math.max(1, Math.ceil(totalRows / 20))}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={handlePreviousPage}
                      disabled={!hasPreviousPage || isLoading}
                      aria-label="Previous page"
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleNextPage}
                      disabled={!hasNextPage || isLoading}
                      aria-label="Next page"
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>

      {/* New Patient Modal */}
      <NewPatientModal
        open={newPatientModalOpen}
        onClose={handleNewPatientClose}
        onSuccessfulSubmission={handlePatientSuccess}
        source="patient_list"
      />
    </Box>
  );
};
